import { useState } from "react";
import { api } from "../api.js";
import { fmt, t } from "../i18n.js";
import { Icon, go, isLead, isManager, useMe } from "../shared.jsx";
import { Empty, ErrorBox, Field, Loader, Pager, useAction, useLoad } from "../ui.jsx";

export function Notifications() {
  const [page, setPage] = useState(1);
  const [unread, setUnread] = useState(false);
  const state = useLoad(() => api.get(`/notifications?page=${page}&page_size=30${unread ? "&unread=1" : ""}`), [page, unread]);
  const { run } = useAction();
  const open = (n) => {
    if (!n.read_at) api.post("/notifications/read", { ids: [n.id] }, { idempotent: false }).catch(() => {});
    if (n.task_id) go(`/tasks/${n.task_id}`);
    else if (n.type === "evaluation_ready") go("/eval");
  };
  return (
    <div className="page">
      <header className="page-head"><h1>{t("notifications")}</h1>
        <div className="actions">
          <label className="check"><input type="checkbox" checked={unread} onChange={(e) => { setUnread(e.target.checked); setPage(1); }} />غير المقروءة فقط</label>
          <button className="btn" onClick={() => run(() => api.post("/notifications/read", {}), "عُلّمت كلها مقروءة").then(state.reload, () => {})}>تعليم الكل كمقروء</button>
        </div></header>
      <Loader state={state} isEmpty={(d) => !d.items.length} emptyView={<Empty title="لا توجد إشعارات" />}>
        {(d) => <>
          <div className="list">{d.items.map((n) => (
            <button key={n.id} className={"row notif" + (n.read_at ? "" : " notif-unread")} onClick={() => open(n)}>
              <div className="row-main"><div className="row-title">{n.title}</div>
                {n.body && <div className="row-sub">{n.body}</div>}</div>
              <small className="muted">{fmt.relative(n.created_at)}</small>
            </button>))}</div>
          <Pager page={d.page} total={d.total} size={d.page_size} onPage={setPage} />
        </>}
      </Loader>
    </div>
  );
}

export function More() {
  const me = useMe();
  const links = [];
  if (isManager(me) || isLead(me)) links.push(["/approvals", "approvals", t("approvals")], ["/reports", "reports", t("reports")]);
  if (isManager(me)) links.push(["/teams", "teams", t("teams")]);
  if (me.role === "admin") links.push(["/people", "people", t("people")], ["/settings", "settings", t("settings")]);
  links.push(["/notifications", "bell", t("notifications")]);
  return (
    <div className="page">
      <header className="page-head"><div><h1>{me.name}</h1><p className="muted">{t("role." + me.role)}{me.job_title ? ` — ${me.job_title}` : ""}{me.branch ? ` — ${me.branch}` : ""}</p></div></header>
      <nav className="more-links">{links.map(([p, icon, label]) => (
        <a key={p} className="row" href={"#" + p}><Icon name={icon} /><span className="row-main">{label}</span></a>))}</nav>
      <NotificationPrefs />
      <ChangePassword />
      <button className="btn btn-block btn-ghost-danger" onClick={() => api.logout()}>{t("logout")}</button>
      <p className="muted small center">تسجيل الخروج يحذف الجلسة والمسودات المحفوظة على هذا الجهاز.</p>
    </div>
  );
}

function NotificationPrefs() {
  const state = useLoad(() => api.get("/notifications/preferences"), []);
  const { busy, run } = useAction();
  return (
    <section className="panel">
      <h2>تفضيلات الإشعارات</h2>
      <Loader state={state}>{(p) => {
        const set = (patch) => state.setData({ ...p, ...patch });
        return (
          <div className="stack">
            <div className="checks">{Object.entries(p.types).map(([k, l]) => (
              <label className="check" key={k}><input type="checkbox" checked={!p.muted_types.includes(k)}
                onChange={(e) => set({ muted_types: e.target.checked ? p.muted_types.filter((x) => x !== k) : [...p.muted_types, k] })} />{l}</label>))}</div>
            <label className="check"><input type="checkbox" checked={p.push_enabled} onChange={(e) => set({ push_enabled: e.target.checked })} />إشعارات الهاتف</label>
            <label className="check"><input type="checkbox" checked={p.lockscreen_preview} onChange={(e) => set({ lockscreen_preview: e.target.checked })} />
              عرض نص الإشعار على شاشة القفل (التقييمات لا تظهر أبدًا)</label>
            <div className="form-grid">
              <Field label="بداية ساعات الهدوء"><input type="time" value={(p.quiet_start || "").slice(0, 5)} onChange={(e) => set({ quiet_start: e.target.value || null })} /></Field>
              <Field label="نهاية ساعات الهدوء"><input type="time" value={(p.quiet_end || "").slice(0, 5)} onChange={(e) => set({ quiet_end: e.target.value || null })} /></Field>
            </div>
            <button className="btn btn-primary" disabled={busy} onClick={() => run(() => api.put("/notifications/preferences", {
              muted_types: p.muted_types, push_enabled: p.push_enabled, lockscreen_preview: p.lockscreen_preview,
              quiet_start: p.quiet_start ? p.quiet_start.slice(0, 5) + ":00" : null, quiet_end: p.quiet_end ? p.quiet_end.slice(0, 5) + ":00" : null,
            }), "حُفظت التفضيلات").then(state.setData, () => {})}>حفظ التفضيلات</button>
          </div>
        );
      }}</Loader>
    </section>
  );
}

function ChangePassword() {
  const [f, setF] = useState({ current_password: "", new_password: "" });
  const { busy, error, run } = useAction();
  return (
    <section className="panel">
      <h2>تغيير كلمة المرور</h2>
      <form className="form-grid" onSubmit={(e) => { e.preventDefault(); run(() => api.post("/auth/change-password", f, { idempotent: false }), "تم تغيير كلمة المرور وتسجيل الخروج من الأجهزة الأخرى").then(() => setF({ current_password: "", new_password: "" }), () => {}); }}>
        <Field label="الحالية"><input type="password" autoComplete="current-password" required value={f.current_password} onChange={(e) => setF({ ...f, current_password: e.target.value })} /></Field>
        <Field label="الجديدة"><input type="password" autoComplete="new-password" required minLength={8} value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} /></Field>
        <div className="span-2"><ErrorBox error={error} /></div>
        <button className="btn" disabled={busy}>تغيير كلمة المرور</button>
      </form>
    </section>
  );
}
